import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { dsmSelectors } from '@usf/ngrx-order';
import { DsmInfo } from '@usf/ngrx-order/lib/models/client/dsm-info';
import { UserService } from '@app/user/services';
import { OrderService } from '@order/services/order.service';
import { PanAppState } from '@panamax/app-state';
import { FEATURES } from '@shared/constants/splitio-features';
import { SellerInfo } from '../my-orders-filters/seller.enum';

export interface SellerFilterState {
  showSellerTab: boolean;
  sellerFilterEnabled: boolean;
  sellers: SellerInfo[];
}

interface DsmState {
  dsmInfo: DsmInfo[];
}

@Injectable({
  providedIn: 'root',
})
export class MyOrdersSellersService {
  private readonly sellerFilterFeatureFlag =
    FEATURES.split_dynamic_order_status_seller_filter.name;
  private readonly INTERNAL_USER_PREFIX = '[AD_AUTO]';

  constructor(
    private store: Store,
    private userService: UserService,
    private orderService: OrderService,
    private panAppState: PanAppState,
  ) {}

  getSellerFilterState$(): Observable<SellerFilterState> {
    return combineLatest([
      this.userService.isSuperUser$(),
      this.orderService.isDsmUser(),
      this.panAppState.feature$([this.sellerFilterFeatureFlag]),
      this.getSellers$(),
    ]).pipe(
      map(([isSuperUser, isDsmUser, sellerFilterEnabled, sellers]) => ({
        showSellerTab: !isSuperUser && isDsmUser,
        sellerFilterEnabled,
        sellers,
      })),
      shareReplay(1),
    );
  }

  private getSellers$(): Observable<SellerInfo[]> {
    return this.store.pipe(
      select(dsmSelectors.selectDsmInfo),
      map((dsmState: DsmState) =>
        this.transformDsmInfoToSellers(dsmState.dsmInfo),
      ),
    );
  }

  private transformDsmInfoToSellers(dsmUsers: DsmInfo[]): SellerInfo[] {
    if (!dsmUsers) {
      return [];
    }
    return dsmUsers.map((dsmUser: DsmInfo) => ({
      id: dsmUser.userName,
      name: `${dsmUser.firstName} ${dsmUser.lastName}`,
      tmId: dsmUser.userName.replace(this.INTERNAL_USER_PREFIX, ''),
      salesRoute: dsmUser.salesRoutes[0].salesRoute.toString(),
      customers: (dsmUser.customers || []).map(dsmCustomer => ({
        id: dsmCustomer.customerNumber.toString(),
        name: dsmCustomer.customerName,
        subtitle: dsmCustomer.customerNumber.toString(),
        city: dsmCustomer.city,
        state: dsmCustomer.state,
        departments: dsmCustomer.departments,
        departmentNumbers: dsmCustomer.departmentNumbers,
      })),
    }));
  }
}
